import { useEffect, useState } from "react";
import BackofficeLayout, { Card, Btn } from "./BackofficeLayout";
import { api } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, ListChecks, Wand2 } from "lucide-react";

const CATS = [
  "loyer_recu", "loyer_verse", "credit_mensualite", "credit_deblocage",
  "charges_energie", "charges_eau", "charges_assurance",
  "charges_generales", "charges_impots", "charges_taxes",
  "solaire_revente", "solaire_charge",
  "travaux_gros_oeuvre", "travaux_vrd", "travaux_etudes",
  "travaux_finitions", "travaux_autres",
  "apport", "distribution", "compte_courant_associe",
  "virement_interne", "frais_bancaires", "revenus_autres",
];

const empty = { entity_id: "", pattern: "", category: "loyer_recu", project_id: "", active: true };

export default function RulesAdmin() {
  const [items, setItems] = useState([]);
  const [entities, setEntities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);

  const load = () =>
    Promise.all([
      api.get("/backoffice/rules").then((r) => r.data),
      api.get("/backoffice/entities").then((r) => r.data),
      api.get("/backoffice/projects").then((r) => r.data),
    ]).then(([r, e, p]) => { setItems(r); setEntities(e); setProjects(p); });
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/backoffice/rules", {
        ...form,
        entity_id: form.entity_id || null,
        project_id: form.project_id || null,
      });
      toast.success("Règle créée");
      setForm(empty); setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette règle ?")) return;
    await api.delete(`/backoffice/rules/${id}`);
    load();
  };

  const apply = async () => {
    const r = await api.post("/backoffice/rules/apply");
    toast.success(`${r.data.recategorized} transactions recatégorisées`);
  };

  const toggle = async (id, active) => {
    await api.put(`/backoffice/rules/${id}`, { active: !active });
    load();
  };

  return (
    <BackofficeLayout
      title="Règles de catégorisation"
      subtitle="Chaque règle applique une catégorie aux transactions dont le libellé correspond au motif (regex ou texte)."
      actions={
        <>
          <Btn variant="ghost" onClick={apply} data-testid="bof-rules-apply"><Wand2 className="w-3.5 h-3.5" /> Appliquer</Btn>
          <Btn onClick={() => setShowForm((s) => !s)} data-testid="bof-rule-new-btn"><Plus className="w-3.5 h-3.5" /> Nouvelle règle</Btn>
        </>
      }
    >
      <Toaster theme="dark" position="top-right" />

      {showForm && (
        <Card className="mb-6" data-testid="bof-rule-form">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input required placeholder='Motif (ex: "loyer|EDF|carrefour")' value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })} className="input md:col-span-2" data-testid="bof-rule-pattern" />
            <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input" data-testid="bof-rule-category">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} className="input">
              <option value="">Toutes entités</option>
              {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="input md:col-span-3">
              <option value="">— Projet (optionnel) —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div className="md:col-span-4 flex gap-2">
              <Btn type="submit" data-testid="bof-rule-save">Créer</Btn>
              <Btn type="button" variant="ghost" onClick={() => setShowForm(false)}>Annuler</Btn>
            </div>
          </form>
          <div className="mt-3 text-xs text-slate-500">
            💡 Le motif est traité comme une regex (insensible à la casse). Ex : <code>edf|engie</code>, <code>loyer.*bail</code>. En cas d&apos;erreur regex, c&apos;est traité comme un simple <em>contient</em>.
          </div>
        </Card>
      )}

      <Card data-testid="bof-rules-list">
        {items.length === 0 ? (
          <div className="text-center text-slate-500 py-8 flex flex-col items-center gap-2">
            <ListChecks className="w-8 h-8 text-slate-700" />
            Aucune règle. Créez-en une pour catégoriser automatiquement vos transactions à l&apos;import CSV.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
              <tr className="border-b border-slate-800">
                <th className="py-2 pr-4">Motif</th>
                <th className="py-2 pr-4">Catégorie</th>
                <th className="py-2 pr-4">Entité</th>
                <th className="py-2 pr-4">Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const ent = entities.find((e) => e.id === r.entity_id);
                return (
                  <tr key={r.id} className="border-b border-slate-800/50" data-testid={`bof-rule-${r.id}`}>
                    <td className="py-2 pr-4 font-mono text-xs">{r.pattern}</td>
                    <td className="py-2 pr-4"><span className="text-xs px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300">{r.category}</span></td>
                    <td className="py-2 pr-4 text-xs text-slate-400">{ent?.name || "Toutes"}</td>
                    <td className="py-2 pr-4">
                      <button onClick={() => toggle(r.id, r.active)} className={`text-xs px-2 py-0.5 rounded border ${r.active ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-slate-800 border-slate-700 text-slate-500"}`}>
                        {r.active ? "Actif" : "Inactif"}
                      </button>
                    </td>
                    <td className="py-2 pr-2">
                      <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <style>{`
        .input { background: rgba(15, 23, 42, 0.6); border: 1px solid rgb(51, 65, 85); color: white;
                 padding: 0.5rem 0.75rem; border-radius: 0.375rem; width: 100%; font-size: 0.875rem; }
        .input:focus { outline: 2px solid rgb(251, 191, 36); outline-offset: -1px; }
      `}</style>
    </BackofficeLayout>
  );
}
